import type { CommentMention } from '../types/reviewComment'

/**
 * 댓글 본문의 "@아이디"를 다루는 공통 규칙.
 *
 * 서버(MentionService)와 같은 규칙을 쓴다. 아이디는 영문·숫자·마침표·언더스코어
 * 3~20자라서 어디서 끝나는지가 명확하고, 마침표/언더스코어로 끝날 수 없다.
 *
 * 정규식에 lookbehind((?<!...))를 쓰지 않는 이유
 * ---------------------------------------------
 * iOS 16.3 이하 사파리가 지원하지 않는다. 지원하지 않는 기기에서는 정규식을 읽는
 * 순간 SyntaxError가 나고, 모듈 최상단이라 앱 전체가 안 뜬다.
 * "@ 앞에 글자가 있으면 이메일" 판정은 직접 앞 글자를 보는 방식으로 처리한다.
 */

/** 아이디로 쓸 수 있는 글자 */
const USERNAME_CHARS = /[A-Za-z0-9._]/

/** 본문에서 "@아이디"를 찾는다 (앞 글자 확인은 별도) */
const MENTION_PATTERN = /@([A-Za-z0-9._]{3,20})/g

/** 문장부호가 붙어 온 경우 뒤를 털어낸다 ("@dy." → "dy") */
function trimTrailing(username: string): string {
  return username.replace(/[._]+$/, '')
}

/**
 * "@" 앞이 아이디 글자면 언급이 아니다.
 * abc@example 같은 이메일이 언급으로 잡히지 않게 한다.
 */
function isMentionBoundary(text: string, atIndex: number): boolean {
  if (atIndex === 0) {
    return true
  }

  return !USERNAME_CHARS.test(text[atIndex - 1])
}

export type MentionSegment =
  | { type: 'text'; text: string }
  | { type: 'mention'; text: string; userId: string }

/**
 * 본문을 일반 글자와 언급으로 쪼갠다.
 *
 * mentions에 없는 "@무언가"는 강조하지 않고 그냥 글자로 둔다.
 * (없는 아이디이거나 맞팔이 아닌 경우 — 판정은 서버가 이미 끝냈다)
 */
export function splitMentions(
  text: string,
  mentions: CommentMention[] | undefined
): MentionSegment[] {
  if (!text) {
    return []
  }
  if (!mentions || mentions.length === 0) {
    return [{ type: 'text', text }]
  }

  // 아이디는 대소문자를 가리지 않고 맞춘다
  const byUsername = new Map(
    mentions.map((m) => [m.username.toLowerCase(), m])
  )

  const segments: MentionSegment[] = []
  let lastIndex = 0

  // 정규식 객체를 재사용하면 lastIndex가 남아 오작동하므로 매번 새로 만든다
  const pattern = new RegExp(MENTION_PATTERN.source, 'g')
  let match: RegExpExecArray | null

  while ((match = pattern.exec(text)) !== null) {
    const start = match.index
    if (!isMentionBoundary(text, start)) {
      continue
    }

    const username = trimTrailing(match[1])
    const found = byUsername.get(username.toLowerCase())
    if (!found) {
      continue // 인정되지 않은 "@무언가" — 글자로 남긴다
    }

    // 문장부호를 털어냈다면 강조 구간도 그만큼 줄여야 위치가 어긋나지 않는다
    const end = start + 1 + username.length

    if (start > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, start) })
    }
    segments.push({
      type: 'mention',
      text: `@${username}`,
      userId: found.userId,
    })
    lastIndex = end
    pattern.lastIndex = end
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) })
  }

  return segments
}

/**
 * 커서 바로 앞에서 입력 중인 "@아이디" 조각을 찾는다.
 *
 * 자동완성을 언제 띄울지 결정하는 데 쓴다. 커서가 언급 도중이 아니면 null.
 */
export function getActiveMentionQuery(
  text: string,
  caret: number
): { keyword: string; start: number } | null {
  // 커서에서 왼쪽으로 훑어 "@"를 찾는다. 아이디에 못 쓰는 글자를 만나면 중단.
  let i = caret - 1
  while (i >= 0) {
    const ch = text[i]
    if (ch === '@') {
      break
    }
    if (!USERNAME_CHARS.test(ch)) {
      return null
    }
    i--
  }

  if (i < 0) {
    return null
  }
  if (!isMentionBoundary(text, i)) {
    return null // 이메일 주소 안이다
  }

  const keyword = text.slice(i + 1, caret)
  if (keyword.length > 20) {
    return null // 아이디 최대 길이를 넘었으면 언급이 아니다
  }

  return { keyword, start: i }
}

/**
 * 입력 중인 "@조각"을 고른 아이디로 바꾼 본문과, 그 뒤로 옮길 커서 위치를 만든다.
 * 뒤에 공백을 붙여 바로 이어서 쓸 수 있게 한다.
 */
export function applyMention(
  text: string,
  caret: number,
  username: string
): { text: string; caret: number } {
  const active = getActiveMentionQuery(text, caret)
  if (!active) {
    return { text, caret }
  }

  const inserted = `@${username} `
  const next = text.slice(0, active.start) + inserted + text.slice(caret)

  return { text: next, caret: active.start + inserted.length }
}
